import useIndustry from "../../../../../hooks/use-industry";
import useQuery from "../../../../../hooks/use-query";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import React, {useCallback, useEffect, useMemo} from "react";
import {Route, Switch, useLocation} from "react-router-dom";
import {Box, Card, MenuItem, TextField, Typography} from "@mui/material";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import LookupAnalyticsDashboardView from "./dashboard";
import LookupAnalyticsLogsView from "./logs";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import {matchPath} from "react-router";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";

const tabs = {
    dashboard: 'dashboard',
    logs: 'logs',
}

const tabList = [
    {
        value: tabs.dashboard,
        label: "Dashboard",
    },
    {
        value: tabs.logs,
        label: "Logs",
    }
]

const LookupAnalyticsView = () => {
    const location = useLocation();
    const [query, setQuery] = useQuery();
    const {loadingIndustries, industries, fetchedIndustry, cachedIndustry, fetchIndustry} = useIndustry({noCategories: true});
    const {configureNotebookTags} = useConfigureNotebook('lookup-analytics-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const tab = useMemo(() => {
        if (matchPath(location.pathname, {path: routes.analytics.lookup.dashboard, exact: true})) {
            return tabs.dashboard;
        }
        if (matchPath(location.pathname, {path: routes.analytics.lookup.logs, exact: true})) {
            return tabs.logs;
        }
        return false;
    }, [location.pathname])

    /**
     * Sets the industry id in the query object
     */
    useEffect(() => {
        if (!tab) {
            return setQuery(prevState => prevState, {location: {pathname: routes.analytics.lookup.dashboard}, replace: true});
        }
        if (!fetchedIndustry?.id) {
            return;
        }
        setQuery(prevState => ({...prevState, industryId: fetchedIndustry?.id}), {replace: true});
    }, [fetchedIndustry?.id, setQuery, tab])

    /**
     * Automatically fetches the cached industry when the component is mounted
     */
    useEffect(() => {
        const selectedIndustryId = query.industryId ?? cachedIndustry?.id;
        if (!selectedIndustryId)
            return;
        void fetchIndustry({industryId: selectedIndustryId});
    }, [cachedIndustry?.id, query.industryId, fetchIndustry]);

    /**
     * Automatically fetches the industry if there is only one industry available and it's not already fetched or selected
     */
    useEffect(() => {
        if (industries?.length === 1) {
            const industry = industries[0];
            if (industry.id !== query.industryId) {
                void fetchIndustry({industryId: industry.id});
            }
        }
    }, [fetchIndustry, industries, query.industryId]);

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.lookupAnalytics);
        configureBehavioralTool((tags) => tags.lookupAnalytics);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Changes the tab in the query object
     * @type {(function(): void)}
     */
    const onTabChanged = useCallback((e, tab) => {
        switch (tab) {
            case tabs.dashboard:
                setQuery(prevState => ({
                    industryId: prevState.industryId,
                    startDate: prevState.startDate,
                    endDate: prevState.endDate,
                }), {location: {pathname: routes.analytics.lookup.dashboard}});
                break;
            case tabs.logs:
                setQuery(prevState => ({
                    ...prevState,
                }), {location: {pathname: routes.analytics.lookup.logs}});
                break;
            default:
                break;
        }
    }, [setQuery])

    return (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBlockEnd: 2}}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    Lookup Analytics
                </Typography>
            </Box>
            {
                industries.length > 1 &&
                <Card variant={'elevation'} sx={{padding: 2, marginBlock: 2}}>
                    <TextField
                        select
                        size={'medium'}
                        label={'Industry'}
                        value={fetchedIndustry ?? ""}
                        onChange={(e) => fetchIndustry({industryId: e.target.value?.id})}
                        fullWidth
                        SelectProps={{renderValue: (value) => value?.title ?? ''}}
                    >
                        {
                            industries.map(industry => (
                                <MenuItem key={industry.id} value={industry}>
                                    {industry.title}
                                </MenuItem>
                            ))
                        }
                    </TextField>
                </Card>
            }
            <div className={'w-100 my-3'}>
                <Tabs value={tab} onChange={onTabChanged}>
                    {
                        tabList?.map((tab) => (
                            <Tab
                                key={tab.value}
                                value={tab.value}
                                label={tab.label}
                            />
                        ))
                    }
                </Tabs>
            </div>
            {
                loadingIndustries &&
                <LoadingLottieAnimation width={'min(90%, 500px)'}/>
            }
            {
                !loadingIndustries && !!query?.industryId &&
                <Switch>
                    <Route path={routes.analytics.lookup.dashboard} exact>
                        <LookupAnalyticsDashboardView/>
                    </Route>
                    <Route path={routes.analytics.lookup.logs} exact>
                        <LookupAnalyticsLogsView/>
                    </Route>
                </Switch>
            }
            {
                !loadingIndustries && !query?.industryId &&
                <Typography variant={'body2'} color={'text.disabled'} fontWeight={600} sx={{textAlign: 'center', marginBlock: 10}}>
                    Please select an industry to view the analytics for it
                </Typography>
            }
        </>
    );
}

export default LookupAnalyticsView;